import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type {
  DashboardInsights,
  RecommendedAction,
  ProgressInsight,
  AdaptationSuggestion,
  StreakInfo,
  ContentRecommendation,
  AIError,
} from '@coffeeplexity/shared';
import type { RootState } from '../index';
import { logger } from '../../utils/logger';

interface SmartDashboardSliceState {
  insights: DashboardInsights | null;
  recommendations: ContentRecommendation[];
  adaptationSuggestions: AdaptationSuggestion[];
  loading: {
    insights: boolean;
    recommendations: boolean;
    adaptations: boolean;
    refresh: boolean;
  };
  error: AIError | null;
  lastUpdated: string | null;
  cacheExpiry: string | null;
  isRefreshing: boolean;
  personalizedGreeting: string | null;
  nextBestAction: RecommendedAction | null;
}

const initialState: SmartDashboardSliceState = {
  insights: null,
  recommendations: [],
  adaptationSuggestions: [],
  loading: {
    insights: false,
    recommendations: false,
    adaptations: false,
    refresh: false,
  },
  error: null,
  lastUpdated: null,
  cacheExpiry: null,
  isRefreshing: false,
  personalizedGreeting: null,
  nextBestAction: null,
};

// Async thunks
export const fetchDashboardInsights = createAsyncThunk(
  'smartDashboard/fetchInsights',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/dashboard/insights/${userId}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch dashboard insights: ${response.statusText}`
        );
      }
      const insights: DashboardInsights = await response.json();
      return insights;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'DASHBOARD_INSIGHTS_ERROR',
        message: error.message || 'Failed to fetch dashboard insights',
        recoverable: true,
        fallbackStrategy: 'generic_experience',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchDashboardRecommendations = createAsyncThunk(
  'smartDashboard/fetchRecommendations',
  async (
    { userId, limit = 5 }: { userId: string; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(
        `/api/ai/dashboard/recommendations/${userId}?limit=${limit}`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch dashboard recommendations: ${response.statusText}`
        );
      }
      const recommendations: ContentRecommendation[] = await response.json();
      return recommendations;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'DASHBOARD_RECOMMENDATIONS_ERROR',
        message: error.message || 'Failed to fetch dashboard recommendations',
        recoverable: true,
        fallbackStrategy: 'curated_content',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchAdaptationSuggestions = createAsyncThunk(
  'smartDashboard/fetchAdaptationSuggestions',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/dashboard/adaptations/${userId}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch adaptation suggestions: ${response.statusText}`
        );
      }
      const adaptations: AdaptationSuggestion[] = await response.json();
      return adaptations;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ADAPTATION_SUGGESTIONS_ERROR',
        message: error.message || 'Failed to fetch adaptation suggestions',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const refreshDashboard = createAsyncThunk(
  'smartDashboard/refresh',
  async (userId: string, { dispatch, rejectWithValue }) => {
    try {
      // Fetch all dashboard data in parallel
      const [insights, recommendations, adaptations] = await Promise.all([
        dispatch(fetchDashboardInsights(userId)).unwrap(),
        dispatch(fetchDashboardRecommendations({ userId })).unwrap(),
        dispatch(fetchAdaptationSuggestions(userId)).unwrap(),
      ]);

      return {
        insights,
        recommendations,
        adaptations,
        timestamp: new Date().toISOString(),
      };
    } catch (error: any) {
      const aiError: AIError = {
        code: 'DASHBOARD_REFRESH_ERROR',
        message: error.message || 'Failed to refresh dashboard',
        recoverable: true,
        fallbackStrategy: 'generic_experience',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const acceptAdaptationSuggestion = createAsyncThunk(
  'smartDashboard/acceptAdaptation',
  async (
    { suggestionId, userId }: { suggestionId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(
        `/api/ai/adaptations/${suggestionId}/accept`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        }
      );
      if (!response.ok) {
        throw new Error(
          `Failed to accept adaptation suggestion: ${response.statusText}`
        );
      }
      return suggestionId;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ADAPTATION_ACCEPT_ERROR',
        message: error.message || 'Failed to accept adaptation suggestion',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const dismissAdaptationSuggestion = createAsyncThunk(
  'smartDashboard/dismissAdaptation',
  async (
    { suggestionId, reason }: { suggestionId: string; reason?: string },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(
        `/api/ai/adaptations/${suggestionId}/dismiss`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      );
      if (!response.ok) {
        throw new Error(
          `Failed to dismiss adaptation suggestion: ${response.statusText}`
        );
      }
      return suggestionId;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ADAPTATION_DISMISS_ERROR',
        message: error.message || 'Failed to dismiss adaptation suggestion',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const trackDashboardAction = createAsyncThunk(
  'smartDashboard/trackAction',
  async (
    {
      actionType,
      actionId,
      userId,
      context,
    }: {
      actionType: string;
      actionId: string;
      userId: string;
      context?: Record<string, any>;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/dashboard/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actionType,
          actionId,
          userId,
          context,
          timestamp: new Date().toISOString(),
        }),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to track dashboard action: ${response.statusText}`
        );
      }
      return { actionType, actionId, timestamp: new Date().toISOString() };
    } catch (error: any) {
      // Don't fail the UI for tracking errors, just log them
      logger.warn('Failed to track dashboard action:', error);
      return { actionType, actionId, timestamp: new Date().toISOString() };
    }
  }
);

const smartDashboardSlice = createSlice({
  name: 'smartDashboard',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearDashboard: state => {
      state.insights = null;
      state.recommendations = [];
      state.adaptationSuggestions = [];
      state.lastUpdated = null;
      state.cacheExpiry = null;
      state.personalizedGreeting = null;
      state.nextBestAction = null;
    },
    updatePersonalizedGreeting: (state, action: PayloadAction<string>) => {
      state.personalizedGreeting = action.payload;
      if (state.insights) {
        state.insights.personalizedGreeting = action.payload;
      }
    },
    markRecommendationViewed: (state, action: PayloadAction<string>) => {
      const recommendation = state.recommendations.find(
        r => r.id === action.payload
      );
      if (recommendation) {
        recommendation.metadata = {
          ...recommendation.metadata,
          viewed: true,
          viewedAt: new Date().toISOString(),
        };
      }
    },
    removeRecommendation: (state, action: PayloadAction<string>) => {
      state.recommendations = state.recommendations.filter(
        r => r.id !== action.payload
      );
    },
    updateStreakInfo: (state, action: PayloadAction<Partial<StreakInfo>>) => {
      if (state.insights) {
        state.insights.streakInfo = {
          ...state.insights.streakInfo,
          ...action.payload,
        };
      }
    },
    addProgressInsight: (state, action: PayloadAction<ProgressInsight>) => {
      if (state.insights) {
        state.insights.progressInsights.unshift(action.payload);
        // Keep only the most recent 10 insights
        if (state.insights.progressInsights.length > 10) {
          state.insights.progressInsights =
            state.insights.progressInsights.slice(0, 10);
        }
      }
    },
    setCacheExpiry: (state, action: PayloadAction<string>) => {
      state.cacheExpiry = action.payload;
    },
    optimisticRemoveAdaptation: (state, action: PayloadAction<string>) => {
      state.adaptationSuggestions = state.adaptationSuggestions.filter(
        suggestion => suggestion.id !== action.payload
      );
    },
  },
  extraReducers: builder => {
    // Fetch Dashboard Insights
    builder
      .addCase(fetchDashboardInsights.pending, state => {
        state.loading.insights = true;
        state.error = null;
      })
      .addCase(fetchDashboardInsights.fulfilled, (state, action) => {
        state.loading.insights = false;
        state.insights = action.payload;
        state.personalizedGreeting = action.payload.personalizedGreeting;
        state.nextBestAction = action.payload.nextBestAction;
        state.lastUpdated = new Date().toISOString();
        state.cacheExpiry = new Date(Date.now() + 15 * 60 * 1000).toISOString(); // 15 minutes
      })
      .addCase(fetchDashboardInsights.rejected, (state, action) => {
        state.loading.insights = false;
        state.error = action.payload as AIError;
      });

    // Fetch Dashboard Recommendations
    builder
      .addCase(fetchDashboardRecommendations.pending, state => {
        state.loading.recommendations = true;
        state.error = null;
      })
      .addCase(fetchDashboardRecommendations.fulfilled, (state, action) => {
        state.loading.recommendations = false;
        state.recommendations = action.payload;
      })
      .addCase(fetchDashboardRecommendations.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.payload as AIError;
      });

    // Fetch Adaptation Suggestions
    builder
      .addCase(fetchAdaptationSuggestions.pending, state => {
        state.loading.adaptations = true;
        state.error = null;
      })
      .addCase(fetchAdaptationSuggestions.fulfilled, (state, action) => {
        state.loading.adaptations = false;
        state.adaptationSuggestions = action.payload;
      })
      .addCase(fetchAdaptationSuggestions.rejected, (state, action) => {
        state.loading.adaptations = false;
        state.error = action.payload as AIError;
      });

    // Refresh Dashboard
    builder
      .addCase(refreshDashboard.pending, state => {
        state.loading.refresh = true;
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(refreshDashboard.fulfilled, (state, action) => {
        state.loading.refresh = false;
        state.isRefreshing = false;
        state.insights = action.payload.insights;
        state.recommendations = action.payload.recommendations;
        state.adaptationSuggestions = action.payload.adaptations;
        state.personalizedGreeting =
          action.payload.insights.personalizedGreeting;
        state.nextBestAction = action.payload.insights.nextBestAction;
        state.lastUpdated = action.payload.timestamp;
        state.cacheExpiry = new Date(Date.now() + 15 * 60 * 1000).toISOString();
      })
      .addCase(refreshDashboard.rejected, (state, action) => {
        state.loading.refresh = false;
        state.isRefreshing = false;
        state.error = action.payload as AIError;
      });

    // Accept Adaptation Suggestion
    builder
      .addCase(acceptAdaptationSuggestion.pending, state => {
        state.loading.adaptations = true;
      })
      .addCase(acceptAdaptationSuggestion.fulfilled, (state, action) => {
        state.loading.adaptations = false;
        state.adaptationSuggestions = state.adaptationSuggestions.filter(
          suggestion => suggestion.id !== action.payload
        );
      })
      .addCase(acceptAdaptationSuggestion.rejected, (state, action) => {
        state.loading.adaptations = false;
        state.error = action.payload as AIError;
      });

    // Dismiss Adaptation Suggestion
    builder
      .addCase(dismissAdaptationSuggestion.pending, state => {
        state.loading.adaptations = true;
      })
      .addCase(dismissAdaptationSuggestion.fulfilled, (state, action) => {
        state.loading.adaptations = false;
        state.adaptationSuggestions = state.adaptationSuggestions.filter(
          suggestion => suggestion.id !== action.payload
        );
      })
      .addCase(dismissAdaptationSuggestion.rejected, (state, action) => {
        state.loading.adaptations = false;
        state.error = action.payload as AIError;
      });

    // Track Dashboard Action (no state changes needed)
    builder.addCase(trackDashboardAction.fulfilled, () => {
      // Action tracked successfully, no state changes needed
    });
  },
});

export const {
  clearError,
  clearDashboard,
  updatePersonalizedGreeting,
  markRecommendationViewed,
  removeRecommendation,
  updateStreakInfo,
  addProgressInsight,
  setCacheExpiry,
  optimisticRemoveAdaptation,
} = smartDashboardSlice.actions;

// Basic selectors
export const selectDashboardInsights = (state: RootState) =>
  state.smartDashboard.insights;
export const selectDashboardRecommendations = (state: RootState) =>
  state.smartDashboard.recommendations;
export const selectAdaptationSuggestions = (state: RootState) =>
  state.smartDashboard.adaptationSuggestions;
export const selectDashboardLoading = (state: RootState) =>
  state.smartDashboard.loading;
export const selectDashboardError = (state: RootState) =>
  state.smartDashboard.error;
export const selectPersonalizedGreeting = (state: RootState) =>
  state.smartDashboard.personalizedGreeting;
export const selectNextBestAction = (state: RootState) =>
  state.smartDashboard.nextBestAction;
export const selectIsRefreshing = (state: RootState) =>
  state.smartDashboard.isRefreshing;

// Memoized selectors
export const selectHighPriorityRecommendations = createSelector(
  [selectDashboardRecommendations],
  recommendations => {
    return recommendations
      .filter(rec => rec.priority >= 0.7)
      .sort((a, b) => b.priority - a.priority);
  }
);

export const selectUnviewedRecommendations = createSelector(
  [selectDashboardRecommendations],
  recommendations => {
    return recommendations.filter(rec => !rec.metadata?.['viewed']);
  }
);

export const selectHighUrgencyAdaptations = createSelector(
  [selectAdaptationSuggestions],
  adaptations => {
    return adaptations.filter(adaptation => adaptation.urgency === 'high');
  }
);

export const selectProgressInsightsByType = createSelector(
  [selectDashboardInsights],
  insights => {
    if (!insights) return {};
    return insights.progressInsights.reduce(
      (acc, insight) => {
        if (!acc[insight.type]) {
          acc[insight.type] = [];
        }
        if (!acc[insight.type]) {
          acc[insight.type] = [];
        }
        acc[insight.type].push(insight);
        return acc;
      },
      {} as Record<string, ProgressInsight[]>
    );
  }
);

export const selectUpcomingMilestones = createSelector(
  [selectDashboardInsights],
  insights => {
    if (!insights) return [];
    return insights.upcomingMilestones
      .filter(milestone => milestone.progress < milestone.target)
      .sort(
        (a, b) =>
          new Date(a.estimatedCompletion).getTime() -
          new Date(b.estimatedCompletion).getTime()
      );
  }
);

export const selectCurrentStreak = createSelector(
  [selectDashboardInsights],
  insights => {
    return insights?.streakInfo?.current || 0;
  }
);

export const selectIsDashboardStale = createSelector(
  [
    selectDashboardInsights,
    (state: RootState) => state.smartDashboard.cacheExpiry,
  ],
  (insights, cacheExpiry) => {
    if (!insights || !cacheExpiry) return true;
    return new Date() > new Date(cacheExpiry);
  }
);

export const selectRecommendationsByType = createSelector(
  [selectDashboardRecommendations],
  recommendations => {
    return recommendations.reduce(
      (acc, rec) => {
        if (!acc[rec.contentType]) {
          acc[rec.contentType] = [];
        }
        if (!acc[rec.contentType]) {
          acc[rec.contentType] = [];
        }
        acc[rec.contentType].push(rec);
        return acc;
      },
      {} as Record<string, ContentRecommendation[]>
    );
  }
);

export default smartDashboardSlice.reducer;
